import { useT, userError } from '../i18n/messages';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PageHeader } from '../components/PageHeader';
import { Button, Card } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';
import { ApiError, billingApi, type ReviewCenterResponse } from '../lib/api';

function describeError(error: unknown) {
  return userError(error);
}

export function ReviewCenterPage() {
  const t = useT();
  const { tokens } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState<{
    status: 'loading' | 'success' | 'error';
    data: ReviewCenterResponse | null;
    error: ApiError | null;
  }>({ status: 'loading', data: null, error: null });
  const [actionState, setActionState] = useState<string | null>(null);

  async function load(signal?: AbortSignal) {
    if (!tokens) {
      return;
    }
    setState({ status: 'loading', data: null, error: null });
    try {
      const data = await billingApi.getReviewCenter(tokens.access_token, signal);
      setState({ status: 'success', data, error: null });
    } catch (error) {
      if (signal?.aborted) {
        return;
      }
      setState({
        status: 'error',
        data: null,
        error:
          error instanceof ApiError
            ? error
            : new ApiError(describeError(error), 500, 'request_failed'),
      });
    }
  }

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => {
      controller.abort();
    };
  }, [tokens?.access_token]);

  async function replayDeadLetters() {
    if (!tokens) {
      return;
    }
    setActionState(t('billing.ui.replaying.dead.lettered.deliveries'));
    const result = await billingApi.replayDeadLetters(tokens.access_token, {
      replay_scope: 'all_dead_lettered',
    });
    setActionState(t('billing.recovery.replayed', { count: result.replayed_count }));
    await load();
  }

  async function rerunReconciliation() {
    if (!tokens) {
      return;
    }
    setActionState(t('billing.ui.running.reconciliation'));
    const result = await billingApi.rerunReconciliation(tokens.access_token, {});
    setActionState(t('billing.recovery.repaired', { count: result.repaired_count }));
    await load();
  }

  if (!tokens) {
    return (
      <section className="panel page-panel">
        {t('billing.ui.your.session.is.missing.or.expired')}
      </section>
    );
  }

  if (state.status === 'loading') {
    return <section className="panel page-panel">{t('billing.ui.loading.data')}</section>;
  }

  if (state.status === 'error' || !state.data) {
    return (
      <Card className="panel panel-error page-panel">
        <p>{userError(state.error, t)}</p>
        <Button onClick={() => void load()} type="button">
          {t('billing.resource.retry')}
        </Button>
      </Card>
    );
  }

  return (
    <ReviewCenterContent
      actionState={actionState}
      data={state.data}
      onOpen={(path) => navigate(path)}
      onReplayDeadLetters={replayDeadLetters}
      onRerunReconciliation={rerunReconciliation}
    />
  );
}

export function ReviewCenterContent({
  actionState,
  data,
  onOpen,
  onReplayDeadLetters,
  onRerunReconciliation,
}: {
  actionState: string | null;
  data: ReviewCenterResponse;
  onOpen(path: string): void;
  onReplayDeadLetters(): void | Promise<void>;
  onRerunReconciliation(): void | Promise<void>;
}) {
  const t = useT();
  const totals = [
    [t('billing.ui.critical'), data.totals.critical],
    [t('billing.ui.warnings'), data.totals.warning],
    [t('billing.ui.info'), data.totals.info],
    [t('billing.ui.total'), data.totals.total],
  ] as const;

  return (
    <section className="page" data-testid="billing-review-center-page">
      <PageHeader title={t('billing.nav.reviewCenter')} />
      <div className="stat-grid">
        {totals.map(([label, value]) => (
          <Card className="stat-card" key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
          </Card>
        ))}
      </div>

      <Card className="panel page-panel">
        <div className="panel-heading">
          <h3>{t('billing.ui.recovery.actions')}</h3>
          <span className="detail-badge">
            {t('billing.review.generated', { date: data.generated_at })}
          </span>
        </div>
        <div className="settings-subgrid">
          <Button onClick={() => void onReplayDeadLetters()} type="button">
            {t('billing.ui.replay.dead.letters')}
          </Button>
          <Button onClick={() => void onRerunReconciliation()} type="button" variant="secondary">
            {t('billing.ui.re.run.reconciliation')}
          </Button>
        </div>
        <p>{actionState ?? t('billing.ui.use.these.actions.after.reviewing.the.items.below')}</p>
      </Card>

      <Card className="panel page-panel">
        <div className="panel-heading">
          <h3>{t('billing.ui.needs.attention')}</h3>
        </div>
        {data.items.length === 0 ? (
          <p>{t('billing.ui.no.review.items.need.attention')}</p>
        ) : (
          <ul className="review-list">
            {data.items.map((item) => (
              <li className={`review-item review-item--${item.severity}`} key={item.id}>
                <div>
                  <span className="detail-badge">{item.severity}</span>
                  <span className="detail-badge">{item.category}</span>
                </div>
                <strong>{item.title}</strong>
                <p>{item.description}</p>
                <dl className="detail-list">
                  <div>
                    <dt>{t('billing.column.status')}</dt>
                    <dd>{item.status}</dd>
                  </div>
                  <div>
                    <dt>{t('billing.resource.updatedAt')}</dt>
                    <dd>{item.created_at}</dd>
                  </div>
                </dl>
                <div className="record-actions">
                  {item.action === 'replay_dead_letters' ? (
                    <Button onClick={() => void onReplayDeadLetters()} type="button">
                      {t('billing.ui.replay')}
                    </Button>
                  ) : null}
                  {item.action === 'rerun_reconciliation' ? (
                    <Button onClick={() => void onRerunReconciliation()} type="button">
                      {t('billing.ui.reconcile')}
                    </Button>
                  ) : null}
                  {item.related_path ? (
                    <Button
                      onClick={() => onOpen(item.related_path ?? '/')}
                      type="button"
                      variant="secondary"
                    >
                      {t('billing.ui.open')}
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </section>
  );
}
